import { Events } from '@platform/client';
import { keepPreviousData } from '@tanstack/react-query';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { Event } from 'lib/client/src/platformapi.schemas';
import { useCallback, useMemo, useState, useContext } from 'react';
import { CreateEventModal } from './CreateEventModal';
import { DeleteEventModal } from './DeleteEventModal';
import { UpdateEventModal } from './UpdateEventModal';
import { AdminTable, AdminTableContext } from '../../AdminTable';
import { AdminActionsContainer, AdminListPageWrapper } from '../../AdminListPage';

const EventsAdmin = () => {
  return (
    <AdminListPageWrapper title={'Events'}>
      <EventsAdminContent />
    </AdminListPageWrapper>
  );
};

const columnHelper = createColumnHelper<Event>();

const EventsAdminContent = () => {
  const { adminQueryOptions, setPagination } = useContext(AdminTableContext);
  const [isCreateEventOpen, setIsCreateEventOpen] = useState(false);
  const [openEvent, setOpenEvent] = useState<Event | undefined>();
  const [eventToDelete, setEventToDelete] = useState<number | undefined>(undefined);

  const { data: eventCount = adminQueryOptions.page } = Events.useCountEventsAdmin();

  const {
    data: events = [],
    isError,
    isLoading,
    isSuccess,
  } = Events.useGetEventsAdmin(adminQueryOptions, {
    query: { placeholderData: keepPreviousData },
  });

  const openEventModal = useCallback(
    (eventId: number) => {
      setOpenEvent(events.find((event) => event.id === eventId));
    },
    [events],
  );

  const openDeleteEventModal = (eventId: number) => {
    setEventToDelete(eventId);
  };

  const closeEventModal = () => {
    setOpenEvent(undefined);
    setIsCreateEventOpen(false);
  };

  const columns = useMemo(
    () =>
      [
        columnHelper.accessor('id', {
          cell: (info) => info.getValue(),
          header: () => <span>Id</span>,
          size: 50,
          enableSorting: true,
        }),
        columnHelper.accessor('title', {
          cell: (info) => info.getValue(),
          header: () => <span>Title</span>,
          size: 150,
          enableSorting: true,
        }),
        columnHelper.accessor('description', {
          cell: (description) => {
            let descriptionText = description.getValue();
            if (descriptionText.length > 150) {
              descriptionText = description.getValue().substring(0, 150) + '...';
            }
            return descriptionText;
          },
          header: () => 'Description',
          size: 350,
          maxSize: 500,
          enableSorting: false,
        }),
        columnHelper.accessor('link', {
          cell: (info) => <div className='text-wrap w-[150px] break-words'>{info.getValue()}</div>,
          header: () => <span>Link</span>,
          size: 150,
          enableSorting: false,
        }),
        columnHelper.accessor('timezone', {
          cell: (timezone) => timezone.getValue(),
          header: () => <span>Timezone</span>,
          size: 150,
          enableSorting: true,
        }),
        columnHelper.accessor('startTime', {
          cell: (startTime) =>
            new Date(startTime.getValue()).toLocaleString([], {
              timeZone: startTime.row.getValue('timezone'),
              month: 'short',
              day: 'numeric',
              year: 'numeric',
              minute: '2-digit',
              hour: '2-digit',
              timeZoneName: 'short',
            }),
          header: () => <span>Start Time</span>,
          size: 300,
          enableSorting: true,
        }),
        columnHelper.accessor('endTime', {
          cell: (endTime) =>
            new Date(endTime.getValue()).toLocaleString([], {
              timeZone: endTime.row.getValue('timezone'),
              month: 'short',
              day: 'numeric',
              year: 'numeric',
              minute: '2-digit',
              hour: '2-digit',
              timeZoneName: 'short',
            }),
          header: () => <span>End Time</span>,
          size: 300,
          enableSorting: true,
        }),
        columnHelper.accessor('hostedBy', {
          cell: (hostedBy) => hostedBy.getValue(),
          header: () => <span>Hosted By</span>,
          size: 150,
          enableSorting: true,
        }),
        columnHelper.accessor('createdAt', {
          cell: (createdAt) => new Date(createdAt.getValue()).toLocaleDateString(),
          header: () => <span>Created At</span>,
          size: 150,
          enableSorting: true,
        }),
        columnHelper.display({
          id: 'Actions',
          header: () => <span>Actions</span>,
          size: 25,
          cell: ({ row }) => (
            <AdminActionsContainer
              actions={[
                {
                  text: 'Edit',
                  type: 'button',
                  onPress: () => openEventModal(row.getValue('id')),
                },
                {
                  text: 'Delete',
                  type: 'button',
                  onPress: () => openDeleteEventModal(row.getValue('id')),
                  linkColor: 'red',
                },
              ]}
            />
          ),
        }),
      ] as ColumnDef<Event>[],
    [openEventModal],
  );

  return (
    <>
      <AdminTable
        data={events}
        columns={columns}
        filteredCount={eventCount}
        isLoading={isLoading}
        isError={isError}
        isSuccess={isSuccess}
        tableActions={[
          {
            callback: () => setIsCreateEventOpen(true),
            text: 'Create event',
          },
        ]}
      />

      <CreateEventModal
        isOpen={isCreateEventOpen}
        onClose={closeEventModal}
        resetPagination={() =>
          setPagination({ pageIndex: adminQueryOptions.page, pageSize: adminQueryOptions.limit })
        }
        limit={adminQueryOptions.limit}
      />
      {openEvent && (
        <UpdateEventModal
          event={openEvent}
          isOpen={!!openEvent}
          onClose={closeEventModal}
          page={adminQueryOptions.page}
          limit={adminQueryOptions.limit}
        />
      )}
      {eventToDelete && (
        <DeleteEventModal
          isOpen={!!eventToDelete}
          onClose={() => setEventToDelete(undefined)}
          id={eventToDelete}
          page={adminQueryOptions.page}
          limit={adminQueryOptions.limit}
        />
      )}
    </>
  );
};

export default EventsAdmin;
